import { useEffect, useRef } from "react";
import { WORLD_BORDERS, WORLD_LAND } from "./worldPath";

/**
 * Vessel Traffic — a VesselFinder-style live AIS view.
 * Real world map, vessels rendered as heading-rotated triangles with fading
 * wakes, moving along curved shipping lanes. Canvas 2D, dpr capped, pauses
 * when the tab is hidden.
 */

const VB_W = 880;
const VB_H = 420;

type P = { x: number; y: number };

const PORTS: (P & { name: string })[] = [
  { x: 216, y: 133, name: "Houston" },
  { x: 331, y: 259, name: "Santos" },
  { x: 451, y: 81, name: "Rotterdam" },
  { x: 448, y: 187, name: "Lagos" },
  { x: 569, y: 144, name: "Jebel Ali" },
  { x: 611, y: 158, name: "Mumbai" },
  { x: 684, y: 200, name: "Singapore" },
  { x: 726, y: 129, name: "Shanghai" },
  { x: 795, y: 282, name: "Sydney" },
];

type Lane = { p: [P, P, P, P]; cargo: string; speed: number };

const bez = (a: number, b: number, c: number, d: number, t: number) => {
  const u = 1 - t;
  return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
};

const pt = (l: Lane, t: number): P => ({
  x: bez(l.p[0].x, l.p[1].x, l.p[2].x, l.p[3].x, t),
  y: bez(l.p[0].y, l.p[1].y, l.p[2].y, l.p[3].y, t),
});

const LANES: Lane[] = [
  {
    p: [
      { x: 216, y: 133 },
      { x: 300, y: 60 },
      { x: 380, y: 52 },
      { x: 451, y: 81 },
    ],
    cargo: "Chemicals",
    speed: 0.000055,
  },
  {
    p: [
      { x: 451, y: 81 },
      { x: 520, y: 90 },
      { x: 550, y: 110 },
      { x: 569, y: 144 },
    ],
    cargo: "Petrochem",
    speed: 0.00008,
  },
  {
    p: [
      { x: 331, y: 259 },
      { x: 480, y: 330 },
      { x: 620, y: 300 },
      { x: 684, y: 200 },
    ],
    cargo: "Ores",
    speed: 0.00004,
  },
  {
    p: [
      { x: 684, y: 200 },
      { x: 740, y: 230 },
      { x: 780, y: 250 },
      { x: 795, y: 282 },
    ],
    cargo: "Metals",
    speed: 0.00006,
  },
];

type Vessel = {
  lane: number;
  t: number;
  dir: 1 | -1;
  speed: number;
  cargo: string;
  label: boolean;
};

/** Free-drifting ships in open water — no wake, just slow heading drift. */
type Drifter = { x: number; y: number; a: number; v: number; turn: number };

const DRIFT_SEEDS: P[] = [
  { x: 150, y: 190 },
  { x: 380, y: 145 },
  { x: 500, y: 250 },
  { x: 700, y: 90 },
  { x: 760, y: 210 },
  { x: 660, y: 355 },
];

const DRIFT_CARGO = [
  "Polymers",
  "Agro",
  "Minerals",
  "Petrochem",
  "Chemicals",
  "Ores",
];

export default function VesselTrafficMap() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const css = getComputedStyle(document.documentElement);
    const primary = css.getPropertyValue("--primary").trim() || "220 90% 56%";
    const bg = css.getPropertyValue("--background").trim() || "0 0% 100%";
    const fg = css.getPropertyValue("--foreground").trim() || "220 20% 10%";
    const P_ = (a: number) => `hsl(${primary} / ${a})`;
    const F_ = (a: number) => `hsl(${fg} / ${a})`;
    const BG = `hsl(${bg})`;

    const land = new Path2D(WORLD_LAND);
    const borders = new Path2D(WORLD_BORDERS);

    let raf = 0;
    let running = true;
    let scale = 1;
    let dpr = 1;

    const vessels: Vessel[] = [];
    LANES.forEach((l, i) => {
      vessels.push({
        lane: i,
        t: Math.random(),
        dir: Math.random() > 0.5 ? 1 : -1,
        speed: l.speed * (0.75 + Math.random() * 0.5),
        cargo: l.cargo,
        label: true,
      });
    });

    const drifters: (Drifter & { cargo: string })[] = DRIFT_SEEDS.map(
      (s, i) => ({
        x: s.x,
        y: s.y,
        a: Math.random() * Math.PI * 2,
        v: 0.0035 + Math.random() * 0.004,
        turn: (Math.random() - 0.5) * 0.00012 + (i % 2 ? 0.00004 : -0.00004),
        cargo: DRIFT_CARGO[i % DRIFT_CARGO.length],
      }),
    );

    const resize = () => {
      dpr = Math.min(1.5, window.devicePixelRatio || 1);
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = (w * VB_H) / VB_W;
      canvas.style.height = `${h}px`;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      scale = w / VB_W;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };

    const drawMap = () => {
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, VB_W, VB_H);

      // sea graticule
      ctx.lineWidth = 0.4;
      ctx.strokeStyle = P_(0.07);
      for (let x = 0; x <= VB_W; x += 44) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, VB_H);
        ctx.stroke();
      }
      for (let y = 0; y <= VB_H; y += 44) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(VB_W, y);
        ctx.stroke();
      }

      ctx.fillStyle = "hsl(220 18% 93%)";
      ctx.strokeStyle = "hsl(220 14% 86%)";
      ctx.lineWidth = 0.6;
      ctx.fill(land);
      ctx.stroke(land);
      ctx.strokeStyle = BG;
      ctx.stroke(borders);
    };

    const drawPorts = (time: number) => {
      PORTS.forEach((p, i) => {
        const pulse = 0.5 + 0.5 * Math.sin(time * 0.0016 + i * 0.9);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5 + pulse * 4, 0, Math.PI * 2);
        ctx.fillStyle = P_(0.05 + pulse * 0.07);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.6, 0, Math.PI * 2);
        ctx.fillStyle = BG;
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = P_(0.9);
        ctx.stroke();

        ctx.font = "7.5px ui-monospace, JetBrains Mono, monospace";
        ctx.fillStyle = F_(0.6);
        ctx.fillText(p.name.toUpperCase(), p.x + 7, p.y + 3);
      });
    };

    const drawVessel = (v: Vessel) => {
      const lane = LANES[v.lane];
      const pos = pt(lane, v.t);
      const ahead = pt(lane, Math.min(1, Math.max(0, v.t + 0.01 * v.dir)));
      const angle = Math.atan2(ahead.y - pos.y, ahead.x - pos.x);

      // AIS triangle
      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(5.4, 0);
      ctx.lineTo(-3.4, 3);
      ctx.lineTo(-3.4, -3);
      ctx.closePath();
      ctx.fillStyle = P_(0.92);
      ctx.fill();
      ctx.restore();

      if (v.label) {
        ctx.font = "6.5px ui-monospace, JetBrains Mono, monospace";
        ctx.fillStyle = F_(0.4);
        ctx.fillText(v.cargo.toUpperCase(), pos.x + 7, pos.y - 5);
      }
    };

    const drawDrifter = (d: Drifter & { cargo: string }) => {
      ctx.save();
      ctx.translate(d.x, d.y);
      ctx.rotate(d.a);
      ctx.beginPath();
      ctx.moveTo(4.4, 0);
      ctx.lineTo(-2.8, 2.5);
      ctx.lineTo(-2.8, -2.5);
      ctx.closePath();
      ctx.fillStyle = P_(0.55);
      ctx.fill();
      ctx.restore();

      ctx.font = "6px ui-monospace, JetBrains Mono, monospace";
      ctx.fillStyle = F_(0.3);
      ctx.fillText(d.cargo.toUpperCase(), d.x + 6, d.y - 4);
    };

    let last = performance.now();
    const frame = (now: number) => {
      if (!running) return;
      const dt = Math.min(48, now - last);
      last = now;

      drawMap();

      for (const v of vessels) {
        v.t += v.speed * v.dir * dt;
        if (v.t > 1) {
          v.t = 1;
          v.dir = -1;
        } else if (v.t < 0) {
          v.t = 0;
          v.dir = 1;
        }
        drawVessel(v);
      }

      for (const d of drifters) {
        d.a += d.turn * dt;
        d.x += Math.cos(d.a) * d.v * dt;
        d.y += Math.sin(d.a) * d.v * dt;
        if (d.x < 6) d.x = VB_W - 6;
        if (d.x > VB_W - 6) d.x = 6;
        if (d.y < 6) d.y = VB_H - 6;
        if (d.y > VB_H - 6) d.y = 6;
        drawDrifter(d);
      }

      drawPorts(now);
      raf = requestAnimationFrame(frame);
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="w-full map-top-fade"
      role="img"
      aria-label="Live vessel traffic map showing cargo ships moving along international shipping lanes between major ports"
    />
  );
}
